<template>
  <div class="login-container">
    <div class="login-wrapper" style="max-width: 500px;">
      <div class="login-right">
        <div class="login-form-container">
          <div class="form-header">
            <h2 class="form-title">{{ $t('two_factor_verification') }}</h2>
            <p class="form-subtitle">{{ $t('enter_verification_code_from_app') }}</p>
          </div>

          <form @submit.prevent="verify" class="login-form">
            <div class="form-group">
              <label class="form-label">{{ $t('verification_code') }}</label>
              <div class="input-wrapper">
                <AppIcon name="bi-shield-lock" class="bi bi-shield-lock input-icon" />
                <input 
                  type="text" 
                  class="form-input text-center" 
                  v-model="code" 
                  :placeholder="$t('enter_6_digit_code')" 
                  maxlength="6" 
                  autofocus
                  required
                >
              </div>
            </div>

            <button 
              type="submit" 
              class="login-btn" 
              :disabled="loading || code.length !== 6"
              :class="{ 'loading': loading }"
            >
              <span v-if="loading" class="loading-spinner"></span>
              <span v-else>{{ $t('verify') }}</span>
            </button>
            
            <div class="links-section">
                 <a href="#" @click.prevent="backToLogin" class="signup-link">{{ $t('back_to_login') }}</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../helper'

export default {
  name: 'TwoFactorAuth',
  data() {
    return {
      code: '',
      loading: false,
      tempToken: null
    }
  },
  mounted() {
    this.tempToken = this.$route.query.token;
    if (!this.tempToken) {
      this.$router.push('/login');
    }
  },
  methods: {
    async verify() {
      this.loading = true;
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/2fa/login`, {
          token: this.code,
          temp_token: this.tempToken
        });

        // Login success
        const user = response.data.user;
        const token = response.data.access_token;

        this.$store.commit('SET_TOKEN', token);
        this.$store.commit('SET_REFRESH_TOKEN', response.data.refresh_token);
        this.$store.commit('SET_USER', user);
        helper.setUserConnectedCookie();
        
        this.$toast.open({
            message: this.$t('logged_in'),
            type: 'success',
            position : 'bottom'
        });
        const savedRedirect = sessionStorage.getItem('post_login_redirect')
        const redirect = savedRedirect && savedRedirect.startsWith('/oauth/authorize?') ? savedRedirect : '/'
        sessionStorage.removeItem('post_login_redirect')
        window.location.href = redirect
        
      } catch (error) {
        this.$toast.open({
            message: this.$t('invalid_code'),
            type: 'error',
            position : 'bottom'
        });
      } finally {
        this.loading = false;
      }
    },
    backToLogin() {
      this.$router.push('/login');
    }
  }
}
</script>

<style scoped>
/* Reuse styles from Login.vue for consistency */
.login-container {
  min-height: 100vh;
  background: #303030;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-wrapper {
  display: flex;
  width: 100%;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(1, 22, 50, 0.15);
}

.login-right {
  flex: 1;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-form-container {
  width: 100%;
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #303030;
  margin-bottom: 10px;
}

.form-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #303030;
  font-size: 1.1rem;
}

.form-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  background: #f9fafb;
}

.form-input:focus {
  outline: none;
  border-color: #303030;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.login-btn {
  background: #004aad;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.links-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  margin-top: 20px;
}

.signup-link {
  color: #303030;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;
}

.signup-link:hover {
  color: #0056d6;
  text-decoration: underline;
}
</style>
